import { Button } from "../../basic/Button";
import { Flex, HStack } from "../../../../../styled-system/jsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Form } from "react-bootstrap";
import FormGroupTile from "../../basic/FormGroupTile";
import { SliderWithMarks } from "../../../fields/base/Slider";
import { useUpdateAdPlan } from "./useUpdateAdPlan";
import { AD_TYPES, ALGORITHM_OPTIONS } from "./constants";
import ConfirmationModal from "../ConfirmationModal";
import AdPlanBiddingRules from "./AdPlanBiddingRules";
import { css } from "../../../../../styled-system/css";
import { Modal } from "../../basic/Modal";
import { HoverInfo } from "../../basic/Tooltip";
import { useSimpleCan } from "../../../../config/casl/casl";
import { Checkbox } from "../../../fields/base/Checkbox";
import useMerchandisePreferences from "../../../../hooks/useMerchandisePreferences";
import { useResolvedLabels } from "../../../../i18n/useResolvedLabels";

const NTMFields = ({ onChange, adPlan, roas }) => {
  const { t } = useTranslation(["advertising"]);
  const { preferences } = useMerchandisePreferences();

  const targetACOS = adPlan?.targetACOS;
  const maxACOS = adPlan?.maxACOS;
  const salesVolumeControl = adPlan?.salesVolumeControl || 4;
  const algorithm = adPlan?.algorithm;

  const rawAlgorithmOptions = [
    ALGORITHM_OPTIONS.ROAS_BOOST,
    ALGORITHM_OPTIONS.GROWTH_MAX,
    ALGORITHM_OPTIONS.SMART_SPEND,
  ];

  rawAlgorithmOptions.push(ALGORITHM_OPTIONS.RULES_ENGINE_ONLY);

  const algorithmOptions = useResolvedLabels(rawAlgorithmOptions, ["advertising"]);

  return (
    <>
      <Row>
        <p className="text-muted fs-standard pb-3">
          {t(
            ($) =>
              $.advertising[
                "Selecting your preferred optimization algorithm and sales volume control will allow the Trellis system to calculate bid-previews. Please note that bids will not be pushed until Ad Management is enabled."
              ],
          )}
        </p>
      </Row>
      <Row>
        <FormGroupTile
          type="select"
          stateName={"algorithm"}
          label={t(($) => $.advertising["Optimization:"])}
          handleChildFormElement={(key, value) => {
            const updatedAlgo = value.value;
            const fieldsToReset = {};

            if (updatedAlgo !== ALGORITHM_OPTIONS.SMART_SPEND.value) {
              fieldsToReset.ignoreSmartSpendACOSRange = false;
            }

            onChange({
              [key]: updatedAlgo,
              ...fieldsToReset,
              crSelectorEnabled: updatedAlgo === ALGORITHM_OPTIONS.ROAS_BOOST.value,
            });
          }}
          disabled={false}
          defaultValue={algorithmOptions.find((a) => a.value === algorithm)}
          options={algorithmOptions}
        />{" "}
        {algorithm === ALGORITHM_OPTIONS.SMART_SPEND.value && (
          <Flex gap={"small"}>
            <Checkbox
              label={t(($) => $.advertising["Ignore {metric} range"], {
                metric: preferences.acos_or_roas.toUpperCase(),
              })}
              value={adPlan.ignoreSmartSpendACOSRange}
              className={css({ marginY: "medium" })}
              onChange={() => {
                onChange({
                  ignoreSmartSpendACOSRange: !adPlan.ignoreSmartSpendACOSRange,
                });
              }}
            />
            <HoverInfo className={css({ display: "inline" })}>
              {preferences.acos_or_roas === "roas" ?
                t(
                  ($) =>
                    $.advertising[
                      "When checked, the algorithm will maximize sales with your full budget even if ROAS goes below your target."
                    ],
                )
              : t(
                  ($) =>
                    $.advertising[
                      "When checked, the algorithm will maximize sales with your full budget even if ACOS goes above your target."
                    ],
                )
              }
            </HoverInfo>
          </Flex>
        )}
      </Row>

      <Row>
        <AdPlanBiddingRules
          state={adPlan}
          onChange={onChange}
          errors={
            adPlan.algorithm === ALGORITHM_OPTIONS.RULES_ENGINE_ONLY.value && !adPlan.biddingProgram ?
              {
                existingBiddingProgram: t(
                  ($) =>
                    $.advertising[
                      "A bidding program is required. Please select an existing program or create a new one."
                    ],
                ),
              }
            : {}
          }
          hideDescription={true}
        />
      </Row>

      <Row>
        <Col>
          <Form.Label
            style={{
              color: "#73706E",
              fontWeight: "700",
              textTransform: "none",
            }}
          >
            {t(($) => $.advertising["Sales Volume Control:"])}
          </Form.Label>
          <SliderWithMarks
            aria-label={t(($) => $.advertising["Sales volume control"])}
            step={1}
            minValue={0}
            maxValue={9}
            marks={[
              { value: 0, label: t(($) => $.advertising["Increase Margin"]) },
              { value: 1, label: t(($) => $.advertising["Optimal Profit"]) },
              { value: 2 },
              { value: 3 },
              { value: 4 },
              { value: 5 },
              { value: 6 },
              { value: 7 },
              { value: 8 },
              { value: 9, label: t(($) => $.advertising["Max Volume"]) },
            ]}
            value={salesVolumeControl}
            onChange={(value) => {
              onChange({
                salesVolumeControl: value,
              });
            }}
          />
        </Col>
      </Row>
      <Row>
        <div className="d-flex">
          <label
            className="m-1 py-2"
            style={{
              color: "#73706E",
              fontWeight: "700",
              textTransform: "none",
              width: "200px",
            }}
          >
            {roas ? t(($) => $.advertising["Optimal ROAS Range"]) : t(($) => $.advertising["Optimal ACOS Range"])}
          </label>
        </div>
        <Col xs={12} md={6} lg={{ span: 4, order: roas ? 2 : 1 }}>
          <FormGroupTile
            type="formcontrol"
            formControlType={"number"}
            stateName={"targetACOS"}
            handleChildFormElement={(key, value) => {
              onChange({ [key]: value });
            }}
            label={roas ? t(($) => $.advertising["High"]) : t(($) => $.advertising["Low"])}
            defaultValue={targetACOS}
          />
        </Col>
        <Col xs={12} md={6} lg={{ span: 4, order: roas ? 1 : 2 }}>
          <FormGroupTile
            type="formcontrol"
            formControlType={"number"}
            stateName={"maxACOS"}
            handleChildFormElement={(key, value) => onChange({ [key]: value })}
            label={roas ? t(($) => $.advertising["Low"]) : t(($) => $.advertising["High"])}
            defaultValue={maxACOS}
          />
        </Col>
      </Row>
    </>
  );
};

const NTMStatusWidget = ({ adPlan, onChange, roas }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const updateAdPlan = useUpdateAdPlan({ adId: adPlan?.id });
  const [showModal, setShowModal] = useState(false);
  const [autoAcceptBids, setAutoAcceptBids] = useState(false);
  const userCanUpdateAds = useSimpleCan("update", "Ad");

  let adPlanStatus = "notEnrolled";
  if (adPlan?.nonTrellisEnrolledAd === undefined) {
    adPlanStatus = "hideWidget";
  }
  if (adPlan?.nonTrellisEnrolledAd) {
    adPlanStatus = "enrolled";
  }
  if (adPlan?.nonTrellisEnrolledAdAcceptBids) {
    adPlanStatus = "bidsAccepted";
  }

  const acceptBids = () => {
    onChange({
      nonTrellisEnrolledAdAcceptBids: true,
      nonTrellisEnrolledAd: true,
    });
    const newAdPlan = {
      ...adPlan,
      nonTrellisEnrolledAdAcceptBids: true,
      nonTrellisEnrolledAd: true,
    };
    updateAdPlan.mutate({ adPlan: newAdPlan });
  };
  const enrollAdPlan = () => {
    onChange({ nonTrellisEnrolledAd: true });
    const newAdPlan = { ...adPlan, nonTrellisEnrolledAd: true };
    updateAdPlan.mutate({ adPlan: newAdPlan });
  };

  if (adPlanStatus === "hideWidget" || !userCanUpdateAds) {
    return null;
  }

  return (
    <div className="border rounded-3 p-4 mb-3 text-center">
      {adPlanStatus === "notEnrolled" && (
        <>
          {showModal && (
            <Modal
              isOpen={showModal}
              onOpenChange={(isOpen) => setShowModal(isOpen)}
              title={
                [AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_DISPLAY_V2.value]?.includes(adPlan?.adType) ?
                  t(($) => $.advertising["Import Targets"])
                : t(($) => $.advertising["Import Keywords and Targets"])
              }
              css={{ width: "50vw", maxHeight: "none", overflowY: "auto", padding: "xlarge" }}
            >
              <div className={css({ overflowY: "auto", maxHeight: "90vh" })}>
                <NTMFields onChange={onChange} adPlan={adPlan} roas={roas} />
                <Row>
                  <Col xs={12} className="mt-5">
                    <Form.Group className="mb-3 mt-3">
                      <Form.Check
                        type="checkbox"
                        label={
                          <>
                            {t(($) => $.advertising["Enable Ad Management"])}{" "}
                            <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                              {t(($) => $.advertising["Selecting this option will push budgets and bids to Amazon"])}
                            </HoverInfo>
                          </>
                        }
                        checked={autoAcceptBids}
                        onChange={(e) => {
                          setAutoAcceptBids(e.target.checked);
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <HStack justifyContent={"center"}>
                  <Button onPress={() => setShowModal(false)} className={css({ marginRight: 8 })}>
                    {t(($) => $.common.actions.Cancel)}
                  </Button>
                  <Button
                    onPress={() => {
                      if (autoAcceptBids) {
                        acceptBids();
                      } else {
                        enrollAdPlan();
                      }
                      setShowModal(false);
                    }}
                    isDisabled={
                      adPlan.algorithm === ALGORITHM_OPTIONS.RULES_ENGINE_ONLY.value && !adPlan.biddingProgram
                    }
                  >
                    {t(($) => $.advertising["Confirm"])}
                  </Button>
                </HStack>
              </div>
            </Modal>
          )}
          <h5>{t(($) => $.advertising["This is a Non-Trellis Managed Campaign"])}</h5>
          <p className="lead mx-auto w-50 text-center">
            {t(
              ($) =>
                $.advertising[
                  "You can import keywords and targets into Trellis by clicking the button below. Once they are imported you can enroll this campaign in Trellis to have it automatically managed by our AI."
                ],
            )}
          </p>
          <Button
            onPress={() => {
              setShowModal(true);
            }}
            className={css({ margin: "auto" })}
          >
            {[AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_DISPLAY_V2.value]?.includes(adPlan?.adType) ?
              t(($) => $.advertising["Import Targets"])
            : t(($) => $.advertising["Import Keywords and Targets"])}
          </Button>
        </>
      )}
      {adPlanStatus === "enrolled" && (
        <>
          <h5>{t(($) => $.advertising["This is a Non Trellis Managed Campaign"])}</h5>
          <p className="lead mx-auto w-50 text-center">
            {t(
              ($) =>
                $.advertising[
                  "You have imported keywords and targets for this campaign. To have Trellis automatically manage your bids and campaign budget, please accept all bids by clicking the Manage Ad button below. Once accepted, values in the Bid Preview column will be automatically applied."
                ],
            )}
          </p>
          <br />
          <br />
          <HStack className={css({ justifyContent: "center" })}>
            <ConfirmationModal
              title={t(($) => $.advertising["Manage Ad"])}
              modalButtonText={t(($) => $.advertising["Manage Ad"])}
              modelButtonStyle={{ transform: "none" }}
              confirmButtonText={t(($) => $.advertising["Confirm"])}
              confirmationMessage={
                <p style={{ fontSize: "1.7rem" }}>
                  {t(
                    ($) =>
                      $.advertising["Are you sure you want to enroll this campaign and automatically accept all bids?"],
                  )}
                </p>
              }
              disabled={adPlan?.servingStatus === "ENDED"}
              onConfirm={() => {
                acceptBids();
              }}
            />
          </HStack>
        </>
      )}
      {adPlanStatus === "bidsAccepted" && (
        <span className="lead">
          {t(
            ($) =>
              $.advertising[
                'Trellis is automatically managing your bids. You can change your settings in the "Advanced Options" section under the Media Plan tab.'
              ],
          )}
        </span>
      )}
    </div>
  );
};

export default NTMStatusWidget;
